import { Markdown } from '@features/markdown/components/Markdown';
import { Breakpoints } from '../../../../settings';
import { Maybe } from '@types';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useSwipeable } from 'react-swipeable';

type Props = {
  image: {
    desktop: any;
    mobile: any;
    alt: string;
  };
  video: {
    desktop: any;
    mobile: any;
    alt?: string;
  };
  title: Maybe<string>;
  description: Maybe<string>;
  tagline: Maybe<string>;
  children?: any;
  isActive: boolean;
  onNextClick: any;
  onPrevClick: any;
  appendSlide?: JSX.Element | React.ReactNode;
  videoClassName?: string;
  bottomNote?: string;
};

const ImageContentSlide = ({
  video,
  videoClassName,
  image,
  title,
  description,
  tagline,
  children,
  isActive,
  onNextClick,
  onPrevClick,
  appendSlide,
  bottomNote,
}: Props) => {
  const isTabletView = useMediaQuery({ maxWidth: Breakpoints.LG });
  const [hidden, setHidden] = useState(!isActive);
  const [videoSrc, setVideoSrc] = useState(video?.desktop?.url ?? video);
  const videoRef = useRef(null);

  useEffect(() => {
    let timeoutId = null;

    if (video && videoRef?.current) {
      timeoutId = setTimeout(() => {
        if (isTabletView && video.mobile) {
          setVideoSrc(video.mobile?.url ?? video.mobile);
        } else {
          setVideoSrc(video.desktop?.url ?? video.desktop);
        }
        videoRef.current.load();
      }, 500);
    }

    return () => {
      !!timeoutId && clearTimeout(timeoutId);
    };
  }, [video, isTabletView]);

  const swipeHandler = useSwipeable({
    onSwipedRight: onPrevClick,
    onSwipedLeft: onNextClick,
    delta: 40,
    preventScrollOnSwipe: false,
    trackTouch: true,
    trackMouse: false,
    rotationAngle: 0,
    swipeDuration: Infinity,
    touchEventOptions: { passive: true },
  });

  useEffect(() => {
    let hideTimeoutId = null;
    let showTimeoutId = null;

    if (!isActive && !hidden) {
      hideTimeoutId = setTimeout(() => {
        if (!isActive) setHidden(true);
        hideTimeoutId = null;
      }, 200);
    } else if (isActive && hidden) {
      showTimeoutId = setTimeout(() => {
        setHidden(false);
        showTimeoutId = null;
      }, 200);
    }

    return () => {
      !!hideTimeoutId && clearTimeout(hideTimeoutId);
      !!showTimeoutId && clearTimeout(showTimeoutId);
    };
  }, [isActive]);

  return (
    <div
      {...swipeHandler}
      className={classNames(
        'tw-flex tw-flex-col tw-items-stretch tw-justify-between tw-gap-6 lg:tw-flex-row lg:tw-justify-center lg:tw-gap-16',
        {
          'tw-hidden': hidden,
        }
      )}>
      <div className='lg:tw-max-w-5/12 tw-order-2 tw-mx-auto tw-flex tw-max-w-[300px] tw-flex-col tw-items-stretch tw-justify-between tw-overflow-x-hidden tw-px-4 tw-text-left md:tw-max-w-sm lg:tw-order-1'>
        {tagline && (
          <h3 className='tw-mb-16 tw-font-body tw-text-base tw-font-extrabold tw-uppercase tw-tracking-wide tw-text-neutrals-dark-grey'>
            {tagline}
          </h3>
        )}
        <div
          className={classNames('tw-animate-[fadeInLeftFull_0.8s] md:tw-min-h-[200px] lg:tw-min-h-[270px]', {
            'tw-animate-[fadeOutLeftFull_0.3s]': !isActive && !hidden,
            // 'tw-animate-[fadeOutLeftFull_0.5s]': !isActive && !hidden && !isTabletView,
          })}>
          {title && (
            <div
              className='tw-mb-5 tw-font-body tw-text-2xl tw-font-bold tw-leading-tight tw-text-brand-black lg:tw-text-[32px]'
              {...(typeof title === 'string' ? { dangerouslySetInnerHTML: { __html: title } } : {})}>
              {typeof title !== 'string' ? title : null}
            </div>
          )}
          {description && (
            <div
              className='tw-max-w-base tw-text-base tw-leading-tight tw-text-neutrals-graphite'
              {...(typeof description === 'string'
                ? { dangerouslySetInnerHTML: { __html: description } }
                : {})}>
              {typeof description !== 'string' ? description : null}
            </div>
          )}
          {!!appendSlide && appendSlide}
          {bottomNote && (
            <Markdown
              source={bottomNote}
              className='tw-mt-5 tw-text-left tw-text-sm tw-font-normal tw-text-muted md:tw-text-base'
            />
          )}
        </div>
        <div className='tw-mt-5'>{children}</div>
      </div>
      <div className='tw-order-1 tw-flex tw-justify-center tw-px-5 lg:tw-order-2 lg:tw-w-7/12'>
        {videoSrc ? (
          <video
            ref={videoRef}
            controls={false}
            title='Product Video'
            height={isTabletView ? 'auto' : '100%'}
            width={isTabletView ? '100%' : 'auto'}
            autoPlay
            loop
            muted
            playsInline
            className={classNames('tw-max-h-[600px] tw-rounded-3xl', videoClassName)}
            poster={
              isTabletView ? image?.mobile?.url ?? image?.mobile : image?.desktop?.url ?? image?.desktop
            }>
            <source src={videoSrc} type='video/mp4' />
          </video>
        ) : (
          <img
            src={isTabletView ? image?.mobile?.url ?? image?.mobile : image?.desktop?.url ?? image?.desktop}
            alt={image?.alt}
            className={classNames(
              'tw-mx-auto tw-max-h-[600px] tw-max-w-full tw-animate-[fadeIn_0.8s] tw-rounded-3xl tw-object-cover lg:tw-mx-auto',
              {
                'tw-animate-[fadeOut_0.3s]': !isActive && !hidden,
              }
            )}
          />
        )}
      </div>
    </div>
  );
};

export default ImageContentSlide;
